import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getClients } from "../api/clients.api";
import { isFeatureEnabled, usePlatformContext } from "./PlatformContext";
import { isUiFeatureEnabled } from "../utils/featureControl";
import { isUnauthorizedError } from "../api/http";
import { logActivity } from "../utils/activity";

const ClientsContext = createContext(null);

let cachedClients = null;
let cachedPagination = null;
let cachedError = null;
let inflightPromise = null;
let cachedAuthError = false;

const toArray = (items) => (Array.isArray(items) ? items : []);

export function ClientsProvider({ children }) {
  const { features, isLoading: platformLoading } = usePlatformContext();
  const clientsEnabled = isFeatureEnabled(features, "clients");
  const uiClientsEnabled = isUiFeatureEnabled("clients");
  const [clients, setClients] = useState(() => toArray(cachedClients));
  const [pagination, setPagination] = useState(() => cachedPagination);
  const [loading, setLoading] = useState(() => !cachedClients);
  const [error, setError] = useState(() => cachedError);

  const fetchClients = useCallback(async ({ force = false } = {}) => {
    if (!clientsEnabled || !uiClientsEnabled || platformLoading) {
      setLoading(false);
      setClients([]);
      setPagination(null);
      setError(null);
      return { items: [], pagination: null };
    }
    if (cachedAuthError) {
      setLoading(false);
      setError(cachedError || new Error("AUTH_REQUIRED"));
      return Promise.reject(cachedError || new Error("AUTH_REQUIRED"));
    }
    if (cachedClients && !force) {
      setLoading(false);
      return { items: cachedClients, pagination: cachedPagination };
    }
    if (inflightPromise && !force) {
      return inflightPromise;
    }
    setLoading(true);
    inflightPromise = getClients()
      .then((result) => {
        const items = toArray(result?.items);
        const pagination = result
          ? {
              page: result.page ?? null,
              pageSize: result.page_size ?? null,
              total: result.total ?? null,
            }
          : null;
        cachedClients = items;
        cachedPagination = pagination;
        cachedError = null;
        setClients(items);
        setPagination(pagination);
        setError(null);
        return { items, pagination };
      })
      .catch((err) => {
        if (isUnauthorizedError(err)) {
          cachedAuthError = true;
        }
        cachedError = err;
        setClients([]);
        setPagination(null);
        setError(err);
        logActivity("api_error", { module: "clients" });
        throw err;
      })
      .finally(() => {
        inflightPromise = null;
        setLoading(false);
      });
    return inflightPromise;
  }, [clientsEnabled, platformLoading, uiClientsEnabled]);

  const refreshClients = useCallback(() => {
    if (!clientsEnabled || !uiClientsEnabled || platformLoading) {
      setLoading(false);
      setClients([]);
      setPagination(null);
      setError(null);
      return Promise.resolve({ items: [], pagination: null });
    }
    if (cachedAuthError) {
      return Promise.reject(cachedError || new Error("AUTH_REQUIRED"));
    }
    cachedClients = null;
    cachedPagination = null;
    cachedError = null;
    return fetchClients({ force: true });
  }, [clientsEnabled, fetchClients, platformLoading, uiClientsEnabled]);

  const upsertClient = useCallback((nextClient) => {
    if (!nextClient?.id) return;
    const current = toArray(cachedClients);
    const updated = current.some((client) => client.id === nextClient.id)
      ? current.map((client) =>
          client.id === nextClient.id ? { ...client, ...nextClient } : client
        )
      : [nextClient, ...current];
    cachedClients = updated;
    setClients(updated);
  }, []);

  const removeClient = useCallback((id) => {
    if (!id) return;
    const updated = toArray(cachedClients).filter((client) => client.id !== id);
    cachedClients = updated;
    setClients(updated);
  }, []);

  useEffect(() => {
    if (!clientsEnabled || !uiClientsEnabled || platformLoading) {
      setLoading(false);
      setClients([]);
      setPagination(null);
      setError(null);
      return;
    }
    fetchClients().catch(() => {});
  }, [clientsEnabled, fetchClients, platformLoading, uiClientsEnabled]);

  const value = useMemo(
    () => ({
      clients,
      pagination,
      loading,
      error,
      refreshClients,
      upsertClient,
      removeClient,
    }),
    [clients, pagination, loading, error, refreshClients, upsertClient, removeClient]
  );

  return (
    <ClientsContext.Provider value={value}>{children}</ClientsContext.Provider>
  );
}

export function useClients() {
  const context = useContext(ClientsContext);
  if (!context) {
    throw new Error("useClients must be used within ClientsProvider");
  }
  return context;
}

export const clearClientsCache = () => {
  cachedClients = null;
  cachedPagination = null;
  cachedError = null;
  inflightPromise = null;
  cachedAuthError = false;
};
